// Positions: read from the location code, distances, and hand-typed coordinates.
import type { Appointment, LatLng } from './types';

export type CoordsResult =
  | { ok: true; lat: number; lng: number }
  | { ok: false; reason: string; raw?: string };

const CODE_RE = /(\d{6})\s*[-_/\s]\s*(\d{6})/;

/** Tunisia, with a margin: a decoded position outside this box is a broken code, not a place. */
export const TUNISIA = { minLat: 30, maxLat: 38, minLng: 7, maxLng: 12 };
export const inTunisia = (lat: number, lng: number) =>
  lat >= TUNISIA.minLat && lat <= TUNISIA.maxLat && lng >= TUNISIA.minLng && lng <= TUNISIA.maxLng;

/**
 * LOC-365833-108559 -> 36.5833, 10.8559; LOC-338645-108125 (Djerba) -> 33.8645, 10.8125.
 * Any position in Tunisia is kept, not only Greater Tunis (36 / 10).
 */
export function parseCoords(a: Pick<Appointment, 'locationCode' | 'address'>): CoordsResult {
  let m = CODE_RE.exec(String(a.locationCode || ''));
  if (!m) {
    m = CODE_RE.exec(String(a.address || ''));
    if (!m) return { ok: false, reason: a.locationCode || a.address ? 'code has no 6+6 digit pair' : 'no location code' };
  }
  const la = m[1], lo = m[2];
  const lat = parseFloat(la.slice(0, 2) + '.' + la.slice(2)), lng = parseFloat(lo.slice(0, 2) + '.' + lo.slice(2));
  if (isNaN(lat) || isNaN(lng)) return { ok: false, reason: 'coordinates could not be read' };
  if (!inTunisia(lat, lng)) return { ok: false, reason: 'coordinates outside Tunisia', raw: la + '-' + lo };
  return { ok: true, lat, lng };
}

export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371, r = Math.PI / 180;
  const dLat = (bLat - aLat) * r, dLng = (bLng - aLng) * r;
  const s = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** "36.8065,10.1815" (comma, semicolon or space) -> {lat, lng}; null when unreadable. */
export function parseLatLng(v: string | null | undefined): LatLng | null {
  const m = /(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)/.exec(String(v ?? '').trim());
  if (!m) return null;
  const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
}

/** Pasted coordinates or a Google Maps link with "@36.8,10.1". `bad` = not in Tunisia. */
export function coordsFromText(t: string): LatLng | { bad: true } | null {
  const m = /(-?\d{1,2}\.\d+)\s*[,;\s]\s*(-?\d{1,3}\.\d+)/.exec(String(t || ''));
  if (!m) return null;
  const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
  if (!inTunisia(lat, lng)) return { bad: true };
  return { lat, lng };
}

export function coordLabel(c: LatLng): string {
  return c.lat.toFixed(4) + ', ' + c.lng.toFixed(4);
}

export function mapsPointUrl(lat: number, lng: number): string {
  return 'https://www.google.com/maps/search/?api=1&query=' + lat.toFixed(5) + ',' + lng.toFixed(5);
}

/** Driving directions from where the phone is to this point. */
export function mapsNavUrl(p: LatLng): string {
  return 'https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=' + p.lat.toFixed(6) + ',' + p.lng.toFixed(6);
}

export function pointInPoly(lat: number, lng: number, poly: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i].lat, xi = poly[i].lng, yj = poly[j].lat, xj = poly[j].lng;
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
